import React, { ReactNode, useMemo } from "react";
import { useApiClient } from "../../api/ApiClient";
import { useSavedContractsContext } from "../landing/SavedContracts";
import { useContractParamsContext } from "../ContractParams";
import { useTracking } from "../../Tracking";
import { useHistory } from "react-router-dom";
import { useAuth } from "../../auth/AuthProvider";
import { CustomVehicleDataFields } from "./CustomVehicleData";
import { ContractViewModel, Role } from "../model/ContractViewModel";
import { getUrls } from "../routing/ContractUrls";

export interface CreateContractRequest {
  role: Role;
  licensePlateNumber?: string;
  finnId?: string;
  customVehicleData?: CustomVehicleDataFields;
}

export interface ICreateContractContext {
  create(request: CreateContractRequest): Promise<void>;
}

export const CreateContractContext =
  React.createContext<ICreateContractContext | null>(null);

export function CreateContractProvider({ children }: { children: ReactNode }) {
  const history = useHistory();
  const client = useApiClient();
  const tracking = useTracking();
  const { setParams } = useContractParamsContext();
  const savedContractsContext = useSavedContractsContext();
  const login = useAuth();
  const { jwt } = login;
  const authorizationHeader = useMemo(
    () => (jwt ? { Authorization: `Bearer ${jwt}` } : undefined),
    [jwt]
  );

  async function create({
    role,
    licensePlateNumber,
    finnId,
    customVehicleData,
  }: CreateContractRequest) {
    const contract = await client.send<ContractViewModel>(
      "POST",
      "contract",
      {
        licensePlateNumber,
        role,
        finnId,
        customVehicleData,
      },
      authorizationHeader
    );
    savedContractsContext.save(contract);
    const token =
      "buyerToken" in contract ? contract.buyerToken : contract.sellerToken;
    const params = { token };
    const urls = getUrls(params);
    history.push(urls.contract);
    setParams(params);
    tracking.contractLoad(contract, role);
  }
  return (
    <CreateContractContext.Provider value={{ create }}>
      {children}
    </CreateContractContext.Provider>
  );
}
